(() => {
  if (customElements.get('reise-product')) return;
  class ReiseProduct extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.pointerNavigation = false;
      this.restockOpening = false;
      this.strings = JSON.parse(this.querySelector('[data-rp-strings]').textContent);
      this.state = JSON.parse(this.querySelector('[data-rp-state]').textContent);
      this.deliveryConfig = JSON.parse(this.querySelector('[data-rp-delivery]')?.textContent || '{"enabled":false}');
      this.shippingCache ||= new Map();
      this.serviceDisclosures = new Map();
      this.form = this.querySelector('.rp-form');
      this.track = this.querySelector('[data-rp-track]');
      this.dock = this.querySelector('[data-rp-dock]');
      this.selected = new Map();
      this.querySelectorAll('[data-rp-options] input:checked').forEach(input => this.selected.set(input.name, input.dataset.optionId));
      this.pendingSizes = new Set();
      this.syncSizeSelection(new URL(location.href));
      this.syncDock();
      this.updateButtons();
      this.syncFavorites();
      this.refreshColorRanges();
      this.addEventListener('click', event => this.onClick(event), { signal: this.signal });
      this.addEventListener('change', event => this.onChange(event), { signal: this.signal });
      this.addEventListener('focusin', event => this.revealPanelFocus(event.target), { signal: this.signal });
      document.addEventListener('pointerdown', () => { this.pointerNavigation = true; }, { capture: true, signal: this.signal });
      document.addEventListener('keydown', () => { this.pointerNavigation = false; }, { capture: true, signal: this.signal });
      this.addEventListener('scroll', event => {
        if (event.target.matches?.('.rp-color-scroll > .rp-color-list')) this.updateColorRange(event.target);
      }, { capture: true, passive: true, signal: this.signal });
      this.addEventListener('input', event => {
        const range = event.target.closest?.('[data-rp-color-range]');
        if (!range) return;
        const list = range.previousElementSibling;
        const max = Math.max(0, list.scrollWidth - list.clientWidth);
        list.scrollTo({ left: max * Number(range.value) / Number(range.max), behavior: 'instant' });
      }, { signal: this.signal });
      this.form.addEventListener('submit', event => this.addToCart(event), { signal: this.signal });
      const shippingForm = this.querySelector('[data-rp-shipping]');
      shippingForm?.addEventListener('submit', event => this.shipping(event), { signal: this.signal });
      shippingForm?.elements.zip.addEventListener('input', () => this.cancelShipping(), { signal: this.signal });
      this.querySelectorAll('.rp-dialog').forEach(dialog => {
        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        }, { signal: this.signal });
        dialog.addEventListener('close', () => this.opener?.focus({ preventScroll: true }), { signal: this.signal });
      });
      this.track?.addEventListener('scroll', () => this.galleryState(), { passive: true, signal: this.signal });
      this.querySelector('[data-rp-gallery-range]')?.addEventListener('input', event => this.goSlide(Number(event.target.value) - 1, 'instant'), { signal: this.signal });
      this.track?.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key) || matchMedia('(min-width:1000px)').matches) return;
        event.preventDefault();
        this.goSlide((this.slide || 0) + (event.key === 'ArrowRight' ? 1 : -1));
      }, { signal: this.signal });
      const scheduleDock = () => {
        if (this.dockFrame) return;
        this.dockFrame = requestAnimationFrame(() => { this.dockFrame = 0; this.updateDock(); });
      };
      window.addEventListener('scroll', scheduleDock, { passive: true, signal: this.signal });
      window.addEventListener('resize', scheduleDock, { passive: true, signal: this.signal });
      window.addEventListener('resize', () => { this.updatePanelPosition(); this.refreshColorRanges(); }, { passive: true, signal: this.signal });
      this.panelObserver = new ResizeObserver(() => { this.updatePanelPosition(); this.refreshColorRanges(this.querySelector('.rp-panel')); });
      this.panelObserver.observe(this.querySelector('.rp-panel'));
      this.galleryObserver = new ResizeObserver(() => this.updatePanelPosition());
      if (this.track) this.galleryObserver.observe(this.track);
      const header = document.querySelector('[data-rn-header]');
      if (header) this.panelObserver.observe(header);
      window.addEventListener('load', () => this.updatePanelPosition(), { once: true, signal: this.signal });
      this.updatePanelPosition();
      window.addEventListener('storage', () => this.syncFavorites(), { signal: this.signal });
      window.addEventListener('popstate', () => this.loadSelection(new URL(location.href), false), { signal: this.signal });
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.updateDeliverySummary();
      }, { signal: this.signal });
      window.addEventListener('message', event => {
        if (event.source !== window || event.origin !== location.origin || !['szb:table-closed', 'szb:plugin-closed'].includes(event.data)) return;
        const opener = this.sizebayOpener;
        this.sizebayOpener = null;
        if (opener?.isConnected && !opener.closest('[hidden]')) opener.focus({ preventScroll: true });
      }, { signal: this.signal });
      this.updateDock();
      this.recentProducts();
    }

    disconnectedCallback() {
      this.cancelShipping();
      this.serviceDisclosures?.forEach((state, details) => {
        state.animation.cancel();
        details.open = state.expanded;
        details.removeAttribute('data-rp-collapsing');
        state.body.inert = false;
      });
      this.serviceDisclosures?.clear();
      this.controller?.abort();
      this.variantRequest?.abort();
      this.panelObserver?.disconnect();
      this.galleryObserver?.disconnect();
      cancelAnimationFrame(this.dockFrame);
      cancelAnimationFrame(this.panelFocusFrame);
      ['--rp-panel-top', '--rp-panel-viewport', '--rp-layout-min-height'].forEach(property => this.style.removeProperty(property));
      document.documentElement.style.removeProperty('--reise-pdp-dock-height');
      this.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
    }

    message(value) {
      this.querySelectorAll('[data-rp-message], [data-rp-dock-message]').forEach(node => { node.textContent = value; });
    }

    syncSizeSelection(url) {
      const explicitValues = new Set((url?.searchParams.get('option_values') || '').split(','));
      this.querySelectorAll('[data-rp-options] [data-rp-size]').forEach(fieldset => {
        const inputs = [...fieldset.querySelectorAll('input')];
        const name = inputs[0]?.name;
        if (!name) return;
        if (url) {
          if (url.searchParams.has('variant') || inputs.some(input => explicitValues.has(input.dataset.optionId))) this.pendingSizes.delete(name);
          else this.pendingSizes.add(name);
        }
        if (this.pendingSizes.has(name)) inputs.forEach(input => { input.checked = false; });
      });
      this.needsSize = this.pendingSizes.size > 0;
    }

    syncDock() {
      const options = this.querySelector('[data-rp-options]').cloneNode(true);
      options.removeAttribute('data-rp-options');
      options.querySelectorAll('[data-rp-sizebay-open]').forEach(button => button.remove());
      options.querySelectorAll('input[data-option-id]').forEach(input => { input.name += '-dock'; });
      this.querySelector('[data-rp-dock-options]').replaceChildren(options);
      const actions = this.querySelector('[data-rp-actions]').cloneNode(true);
      actions.removeAttribute('data-rp-actions');
      actions.querySelector('[data-rp-favorite]')?.remove();
      const buy = actions.querySelector('.rp-buy');
      buy.type = 'button';
      buy.dataset.rpDockBuy = '';
      this.querySelector('[data-rp-dock-actions]').replaceChildren(actions);
      const price = this.querySelector('[data-rp-price]').cloneNode(true);
      this.dock.querySelector('[data-rp-price]').replaceWith(price);
    }

    updateButtons() {
      const soldOut = !!this.state.variantId && !this.state.available && (this.state.productAvailable === false || !this.needsSize);
      const blocked = this.loading || this.adding || this.selectionError || soldOut || (!this.needsSize && !this.state.variantId);
      if (this.restockVariantId !== this.state.variantId) {
        this.restockVariantId = this.state.variantId;
        // Notify Me observes the variant every 100 ms, then debounces for 400 ms.
        this.restockReadyAt = performance.now() + 600;
      }
      this.querySelectorAll('.rp-actions').forEach(actions => actions.classList.toggle('is-sold-out', soldOut));
      this.querySelectorAll('.rp-buy').forEach(button => {
        button.disabled = !!blocked;
        button.setAttribute('aria-busy', String(!!(this.loading || this.adding)));
        button.textContent = this.loading || this.adding ? this.strings.loading : this.selectionError ? this.strings.error : soldOut ? this.strings.soldOut : !this.needsSize && !this.state.variantId ? this.strings.unavailable : this.strings.buy;
      });
      this.querySelectorAll('.rp-option').forEach(fieldset => { fieldset.disabled = !!this.adding; });
      this.form.querySelector('[data-rp-variant]').value = this.state.variantId || '';
      this.querySelectorAll('[data-rp-notify]').forEach(button => {
        button.hidden = !soldOut;
        button.disabled = !!(this.loading || this.selectionError || this.restockOpening);
        button.setAttribute('aria-busy', String(!!this.restockOpening));
      });
      this.setShippingBusy(!!this.shippingRequest);
      this.updateDeliverySummary();
    }

    updateDeliverySummary() {
      const title = this.querySelector('[data-rp-delivery-title]');
      if (!title) return;
      const description = this.querySelector('[data-rp-delivery-description]');
      const config = this.deliveryConfig;
      const active = config.enabled && this.state.available && this.state.variantId && !this.loading && !this.selectionError;
      if (!active) {
        title.textContent = this.strings.delivery;
        description.textContent = this.strings.deliveryDescription;
        return;
      }

      // Use the store's calendar day, independently of the shopper's time zone.
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(new Date());
      const calendar = Object.fromEntries(parts.map(part => [part.type, part.value]));
      const today = Date.UTC(Number(calendar.year), Number(calendar.month) - 1, Number(calendar.day), 12);
      const addBusinessDays = days => {
        const date = new Date(today);
        for (let added = 0; added < days;) {
          date.setUTCDate(date.getUTCDate() + 1);
          if (![0, 6].includes(date.getUTCDay())) added++;
        }
        return date;
      };
      // Preserve the existing Reise rule: continue/allow OR stock <= 0 adds production time.
      const madeToOrder = ['continue', 'allow'].includes(this.state.inventoryPolicy) || (Number.isFinite(this.state.inventoryQuantity) && this.state.inventoryQuantity <= 0);
      const production = madeToOrder ? config.productionDays : 0;
      const start = addBusinessDays(config.transitMin + production);
      const end = addBusinessDays(config.transitMax + production);
      const locale = document.documentElement.lang || 'pt-BR';
      const format = (date, year = false) => new Intl.DateTimeFormat(locale, { timeZone: 'UTC', day: 'numeric', month: 'short', ...(year ? { year: 'numeric' } : {}) }).format(date);
      const free = config.currency === 'BRL' && config.freeThreshold > 0 && Number.isFinite(this.state.price) && this.state.price >= config.freeThreshold;
      title.textContent = (free ? this.strings.deliveryEstimateFree : this.strings.deliveryEstimate)
        .replace('[start]', format(start)).replace('[end]', format(end, start.getUTCFullYear() !== end.getUTCFullYear()));
      description.textContent = this.strings.deliveryDisclaimer;

    }

    onChange(event) {
      const input = event.target.closest('input[data-option-id]');
      if (!input) return;
      const name = input.name.replace(/-dock$/, '');
      this.selected.set(name, input.dataset.optionId);
      if (input.closest('[data-rp-size]')) {
        this.pendingSizes.delete(name);
        this.needsSize = this.pendingSizes.size > 0;
      }
      const url = new URL(input.dataset.productUrl || this.dataset.productUrl, location.origin);
      if (url.pathname !== new URL(this.dataset.productUrl, location.origin).pathname) {
        url.searchParams.set('view', 'reise-v5');
        location.assign(url);
        return;
      }
      const current = new URL(location.href);
      url.search = current.search;
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', [...this.selected.values()].join(','));
      this.loadSelection(url, true, { name, id: input.dataset.optionId, dock: input.name.endsWith('-dock') });
    }

    async loadSelection(url, updateHistory, focus) {
      this.cancelShipping();
      this.variantRequest?.abort();
      const request = new AbortController();
      this.variantRequest = request;
      this.loading = true;
      this.selectionError = false;
      this.message('');
      this.updateButtons();
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const response = await fetch(fetchUrl, { signal: request.signal });
        if (!response.ok) throw new Error(this.strings.error);
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = document.querySelector('reise-product');
        if (request.signal.aborted || !this.isConnected) return;
        if (!next?.querySelector('[data-rp-state]') || !next.querySelector('[data-rp-options]') || !next.querySelector('[data-rp-price]')) throw new Error(this.strings.error);
        this.state = JSON.parse(next.querySelector('[data-rp-state]').textContent);
        this.querySelector('[data-rp-options]').replaceWith(next.querySelector('[data-rp-options]'));
        this.querySelector('[data-rp-price]').replaceWith(next.querySelector('[data-rp-price]'));
        this.selected.clear();
        this.querySelectorAll('[data-rp-options] input:checked').forEach(input => this.selected.set(input.name, input.dataset.optionId));
        this.syncSizeSelection(updateHistory ? null : url);
        if (updateHistory) {
          const historyUrl = new URL(url);
          historyUrl.searchParams.delete('section_id');
          if (this.state.variantId && !this.needsSize) {
            historyUrl.searchParams.delete('option_values');
            historyUrl.searchParams.set('variant', this.state.variantId);
          } else {
            const choices = [...this.selected].filter(([name]) => !this.pendingSizes.has(name)).map(([, id]) => id);
            historyUrl.searchParams.delete('variant');
            if (choices.length) historyUrl.searchParams.set('option_values', choices.join(','));
            else historyUrl.searchParams.delete('option_values');
          }
          history.replaceState({}, '', historyUrl);
        }
        this.syncDock();
        this.refreshColorRanges();
        this.syncFavorites();
        if (focus) this.querySelector(`input[name="${CSS.escape(focus.name + (focus.dock ? '-dock' : ''))}"][data-option-id="${CSS.escape(focus.id)}"]`)?.focus({ preventScroll: true });
        const media = this.querySelector(`[data-media-id="${this.state.mediaId}"]`);
        if (media && !matchMedia('(min-width:1000px)').matches) this.goSlide([...this.track.children].indexOf(media));
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.selectionError = true;
        this.message(this.strings.error);
      } finally {
        if (this.variantRequest === request) { this.loading = false; this.updateButtons(); }
      }
    }

    async addToCart(event) {
      event.preventDefault();
      if (this.loading || this.adding || this.selectionError) return;
      if (this.needsSize) {
        this.message(this.strings.choose);
        const scope = this.dock.hidden ? this.querySelector('[data-rp-options]') : this.querySelector('[data-rp-dock-options]');
        [...scope.querySelectorAll('[data-rp-size] input')].find(input => this.pendingSizes.has(input.name.replace(/-dock$/, '')))?.focus({ preventScroll: !this.dock.hidden });
        return;
      }
      if (!this.state.available || !this.state.variantId) return;
      this.adding = true;
      const addedVariantId = this.state.variantId;
      const cartTrigger = event.submitter || event.target.closest('[data-rp-dock-buy]') || this.form.querySelector('[type="submit"]');
      this.updateButtons();
      this.message('');
      try {
        const response = await fetch(`${this.dataset.rootUrl}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: addedVariantId, quantity: 1 }] })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.strings.error);
        const handled = !this.dispatchEvent(new CustomEvent('reise:cart-added', { bubbles: true, cancelable: true, detail: { variantId: addedVariantId, trigger: cartTrigger } }));
        if (!handled) {
          location.assign(this.dataset.cartUrl);
        }
      } catch (error) { this.message(error.message || this.strings.error); }
      finally { this.adding = false; this.updateButtons(); }
    }

    onClick(event) {
      const serviceSummary = event.target.closest('.rp-service > summary');
      if (serviceSummary) {
        event.preventDefault();
        this.toggleService(serviceSummary.parentElement);
        return;
      }
      const target = event.target.closest('button, a');
      if (!target) return;
      if (target.tagName === 'A' && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
      if (target.hasAttribute('data-rp-sizebay-open')) this.openSizebay(target);
      if (target.hasAttribute('data-rp-notify')) this.openRestock(target);
      if (target.hasAttribute('data-rp-dock-buy')) this.addToCart(event);
      if (target.hasAttribute('data-rp-favorite')) this.toggleFavorite();
      if (target.hasAttribute('data-rp-code')) this.copyCode(target);
      if (target.hasAttribute('data-rp-close')) target.closest('dialog').close();
      if (target.hasAttribute('data-rp-open')) {
        const dialog = this.querySelector(`[data-rp-dialog="${target.dataset.rpOpen}"]`);
        if (dialog) { event.preventDefault(); this.openDialog(dialog, target); }
      }
      if (target.hasAttribute('data-rp-zoom')) {
        const viewer = this.querySelector('reise-media-viewer');
        if (typeof viewer?.open !== 'function') return;
        event.preventDefault();
        viewer.open(Number(target.dataset.rpMediaIndex), target);
      }
    }

    toggleService(details) {
      const body = details.querySelector(':scope > .rp-shipping-form, :scope > .rp-returns-body');
      if (!body) return;
      const previous = this.serviceDisclosures.get(details);
      const expanded = !(previous?.expanded ?? details.open);
      const styles = previous ? getComputedStyle(body) : null;
      const start = {
        opacity: styles ? styles.opacity : expanded ? 0 : 1,
        transform: styles ? styles.transform : expanded ? 'translateY(-4px)' : 'none'
      };
      previous?.animation.cancel();
      body.inert = false;
      details.removeAttribute('data-rp-collapsing');
      if (!body.animate || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        details.open = expanded;
        this.serviceDisclosures.delete(details);
        return;
      }
      details.open = true;
      details.toggleAttribute('data-rp-collapsing', !expanded);
      if (!expanded && body.contains(document.activeElement)) details.querySelector('summary').focus({ preventScroll: true });
      body.inert = !expanded;
      const animation = body.animate([start, {
        opacity: expanded ? 1 : 0,
        transform: expanded ? 'none' : 'translateY(-4px)'
      }], { duration: 180, easing: 'cubic-bezier(.2, .7, .2, 1)' });
      const state = { expanded, animation, body };
      this.serviceDisclosures.set(details, state);
      animation.finished.then(() => {
        if (this.serviceDisclosures.get(details) !== state) return;
        details.open = expanded;
        details.removeAttribute('data-rp-collapsing');
        body.inert = false;
        animation.cancel();
        this.serviceDisclosures.delete(details);
      }).catch(() => {});
    }

    async openRestock(opener) {
      if (this.loading || this.selectionError || this.restockOpening || this.state.available || !this.state.variantId) return;
      if (this.needsSize) {
        this.message(this.strings.choose);
        const scope = opener.closest('.rp-dock') || this.querySelector('[data-rp-options]');
        [...scope.querySelectorAll('[data-rp-size] input')].find(input => this.pendingSizes.has(input.name.replace(/-dock$/, '')))?.focus();
        return;
      }
      const variantId = this.state.variantId;
      const signal = this.signal;
      const bridge = this.querySelector('[data-rp-restock-bridge]');
      this.restockOpening = true;
      this.message('');
      this.updateButtons();
      try {
        const ready = await new Promise(resolve => {
          const finish = value => {
            clearInterval(timer);
            signal.removeEventListener('abort', cancel);
            resolve(value);
          };
          const cancel = () => finish(false);
          const deadline = performance.now() + 8000;
          const timer = setInterval(() => {
            if (this.loading || this.needsSize || this.state.variantId !== variantId || this.state.available) return finish(false);
            if (performance.now() >= this.restockReadyAt && bridge?.style.display === 'flex') return finish(true);
            if (performance.now() >= deadline) finish(false);
          }, 50);
          signal.addEventListener('abort', cancel, { once: true });
        });
        if (signal.aborted || this.loading || this.needsSize || this.state.variantId !== variantId || this.state.available) return;
        if (ready) bridge.click();
        else this.message(this.strings.restockUnavailable);
      } finally {
        if (this.signal === signal) {
          this.restockOpening = false;
          if (!signal.aborted) this.updateButtons();
        }
      }
    }

    async openSizebay(opener) {
      if (this.sizebayOpening) return;
      const mount = this.querySelector('[data-rp-sizebay]');
      if (!mount) return;
      this.sizebayOpening = true;
      opener.setAttribute('aria-busy', 'true');
      try {
        let button = mount.querySelector('#szb-measurements-button');
        if (!button) {
          this.message(this.strings.sizebayLoading);
          button = await new Promise(resolve => {
            const finish = value => { observer.disconnect(); clearTimeout(timer); this.signal.removeEventListener('abort', cancel); resolve(value); };
            const cancel = () => finish(null);
            const observer = new MutationObserver(() => {
              const nativeButton = mount.querySelector('#szb-measurements-button');
              if (nativeButton) finish(nativeButton);
            });
            const timer = setTimeout(cancel, 10000);
            observer.observe(mount, { childList: true, subtree: true });
            this.signal.addEventListener('abort', cancel, { once: true });
          });
        }
        if (this.signal.aborted) return;
        if (button) { this.message(''); this.sizebayOpener = opener; button.click(); }
        else this.message(this.strings.sizebayUnavailable);
      } finally {
        this.sizebayOpening = false;
        opener.removeAttribute('aria-busy');
      }
    }

    openDialog(dialog, opener) {
      this.opener = opener;
      dialog.showModal();
    }

    goSlide(index, behavior) {
      const slide = Math.max(0, Math.min(index, this.track.children.length - 1));
      this.track.scrollTo({ left: this.track.clientWidth * slide, behavior: behavior || (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth') });
    }

    galleryState() {
      this.slide = Math.round(this.track.scrollLeft / this.track.clientWidth);
      const range = this.querySelector('[data-rp-gallery-range]');
      if (range) {
        range.value = String(this.slide + 1);
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const firstSlide = 1 / this.track.children.length;
        const progress = firstSlide + (maxScroll > 0 ? this.track.scrollLeft / maxScroll * (1 - firstSlide) : 0);
        range.style.setProperty('--rp-gallery-progress', `${Math.min(100, Math.max(0, progress * 100))}%`);
      }
    }

    refreshColorRanges(scope = this) {
      scope.querySelectorAll('.rp-color-scroll > .rp-color-list').forEach(list => this.updateColorRange(list));
    }

    updateColorRange(list) {
      const range = list.nextElementSibling;
      if (!range?.matches('[data-rp-color-range]')) return;
      const max = Math.max(0, list.scrollWidth - list.clientWidth);
      const visible = max > 1 && list.clientWidth > 0 && !list.closest('[hidden]');
      range.hidden = !visible;
      list.classList.toggle('is-enhanced', visible);
      if (!visible) return;
      const firstColor = list.querySelector('.rp-color');
      const step = (firstColor?.getBoundingClientRect().width || list.clientWidth) + (parseFloat(getComputedStyle(list).gap) || 0);
      range.max = String(Math.max(1, Math.round(max / step)));
      const position = Math.min(1, Math.max(0, list.scrollLeft / max));
      const coverage = Math.min(1, list.clientWidth / list.scrollWidth);
      range.value = String(Math.round(position * Number(range.max)));
      range.style.setProperty('--rp-color-progress', `${((coverage + (1 - coverage) * position) * 100).toFixed(2)}%`);
      range.style.setProperty('--rp-color-visible', `${(coverage * 100).toFixed(2)}%`);
    }

    updatePanelPosition() {
      this.updateGalleryLayout();
      const panel = this.querySelector('.rp-panel');
      const gallery = this.querySelector('.rp-gallery');
      if (!panel || !gallery) return;
      const header = document.querySelector('[data-rn-header]');
      const offset = header?.getBoundingClientRect().height ?? (parseFloat(getComputedStyle(this).getPropertyValue('--rp-header-offset')) || 0);
      const viewport = Math.max(0, innerHeight - offset);
      const galleryHeight = gallery.getBoundingClientRect().height;
      const sticky = matchMedia('(min-width: 1000px)').matches && viewport > 0 && galleryHeight > viewport;
      panel.classList.toggle('rp-panel--sticky', sticky);
      if (!sticky) {
        ['--rp-panel-top', '--rp-panel-viewport', '--rp-layout-min-height'].forEach(property => this.style.removeProperty(property));
        return;
      }
      this.style.setProperty('--rp-panel-top', `${offset}px`);
      this.style.setProperty('--rp-panel-viewport', `${viewport}px`);
      // A tall panel needs room to reveal its remaining content after the final photo.
      const overflow = Math.max(0, panel.getBoundingClientRect().height - viewport);
      this.style.setProperty('--rp-layout-min-height', `${galleryHeight + overflow}px`);
    }

    revealPanelFocus(target) {
      if (this.pointerNavigation || !target.closest?.('.rp-panel--sticky') || target.closest('dialog[open]')) return;
      cancelAnimationFrame(this.panelFocusFrame);
      this.panelFocusFrame = requestAnimationFrame(() => {
        if (this.pointerNavigation || !target.isConnected || document.activeElement !== target) return;
        const panel = target.closest('.rp-panel--sticky');
        if (!panel) return;
        const top = parseFloat(getComputedStyle(panel).top) || 0;
        const rect = target.getBoundingClientRect();
        const bottom = innerHeight - (this.dock.hidden ? 16 : this.dock.getBoundingClientRect().height + 16);
        if (rect.bottom > bottom) {
          const release = this.querySelector('.rp-layout').getBoundingClientRect().bottom + scrollY - panel.getBoundingClientRect().height - top;
          window.scrollTo({ top: Math.max(scrollY, release) + rect.top - top - 16, behavior: 'instant' });
        } else if (rect.top < top) {
          window.scrollTo({ top: scrollY + rect.top - top - 16, behavior: 'instant' });
        }
      });
    }

    updateGalleryLayout() {
      const gallery = this.querySelector('.rp-gallery');
      const first = this.track?.querySelector('.rp-slide');
      if (!gallery || !first) return;
      gallery.style.setProperty('--rp-gallery-top', `${Math.max(0, Math.round(gallery.getBoundingClientRect().top + window.scrollY))}px`);
      gallery.style.setProperty('--rp-gallery-row-height', `${first.getBoundingClientRect().height}px`);
    }

    updateDock() {
      const rect = this.querySelector('[data-rp-actions]').getBoundingClientRect();
      const offset = parseFloat(getComputedStyle(this).getPropertyValue('--rp-header-offset')) || 0;
      const footer = document.querySelector('.rn-footer, .wrapper-footer, footer.preview-footer');
      const wasHidden = this.dock.hidden;
      this.dock.hidden = this.dataset.sticky !== 'true' || rect.bottom > offset || (footer && footer.getBoundingClientRect().top < innerHeight);
      if (wasHidden !== this.dock.hidden) this.refreshColorRanges(this.dock);
      if (!this.dock.hidden) {
        const height = Math.ceil(this.dock.getBoundingClientRect().height);
        if (height !== this.dockHeight) {
          this.dockHeight = height;
          document.documentElement.style.setProperty('--reise-pdp-dock-height', `${height}px`);
        }
      }
    }

    syncFavorites() {
      try {
        const saved = JSON.parse(localStorage.getItem('wishlistItem') || '[]');
        this.querySelectorAll('[data-rp-favorite]').forEach(button => button.setAttribute('aria-pressed', String(Array.isArray(saved) && saved.includes(this.dataset.productHandle))));
      } catch { this.querySelectorAll('[data-rp-favorite]').forEach(button => button.setAttribute('aria-pressed', 'false')); }
    }

    toggleFavorite() {
      try {
        const existing = JSON.parse(localStorage.getItem('wishlistItem') || '[]');
        const list = Array.isArray(existing) ? existing : [];
        const handle = this.dataset.productHandle;
        const present = list.includes(handle);
        localStorage.setItem('wishlistItem', JSON.stringify(present ? list.filter(item => item !== handle) : [...list, handle]));
        this.syncFavorites();
        document.querySelectorAll('[data-wishlist-count]').forEach(node => { node.textContent = String(present ? list.length - 1 : list.length + 1); });
        this.message(present ? this.strings.removed : this.strings.saved);
      } catch { this.message(this.strings.storageError); }
    }

    async copyCode(button) {
      try { await navigator.clipboard.writeText(button.dataset.rpCode); this.message(this.strings.copied); }
      catch { this.message(`${this.strings.copyError} ${button.dataset.rpCode}`); }
    }

    setShippingBusy(busy) {
      const form = this.querySelector('[data-rp-shipping]');
      if (!form) return;
      const button = form.querySelector('button');
      button.disabled = busy || !!this.loading || !!this.selectionError || !this.state.available || !this.state.variantId;
      button.setAttribute('aria-busy', String(busy));
      form.setAttribute('aria-busy', String(busy));
      form.querySelector('[data-rp-shipping-result]').setAttribute('aria-busy', String(busy));
    }

    cancelShipping() {
      const request = this.shippingRequest;
      this.shippingRequest = null;
      clearTimeout(this.shippingTimeout);
      request?.abort();
      const form = this.querySelector('[data-rp-shipping]');
      form?.querySelector('[data-rp-shipping-result]').replaceChildren();
      form?.elements.zip.removeAttribute('aria-invalid');
      this.setShippingBusy(false);
    }

    async shippingAddress(zip, signal) {
      const providers = [
        { url: `https://viacep.com.br/ws/${zip}/json/`, state: 'uf', city: 'localidade' },
        { url: `https://brasilapi.com.br/api/cep/v1/${zip}`, state: 'state', city: 'city' }
      ];
      for (const provider of providers) {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        const lookup = new AbortController();
        const abort = () => lookup.abort();
        signal.addEventListener('abort', abort, { once: true });
        const timeout = setTimeout(abort, 3500);
        let data;
        try {
          const response = await fetch(provider.url, { credentials: 'omit', signal: lookup.signal });
          if ([400, 404].includes(response.status)) data = { erro: true };
          else {
            if (!response.ok) throw new Error('lookup');
            data = await response.json();
          }
        } catch (error) {
          if (signal.aborted) throw error;
          continue;
        } finally {
          clearTimeout(timeout);
          signal.removeEventListener('abort', abort);
        }
        const provinceCode = String(data?.[provider.state] || '').trim().toUpperCase();
        if (data?.erro || !/^(AC|AL|AP|AM|BA|CE|DF|ES|GO|MA|MT|MS|MG|PA|PB|PR|PE|PI|RJ|RN|RS|RO|RR|SC|SP|SE|TO)$/.test(provinceCode)) throw new Error('invalid-zip');
        const city = typeof data[provider.city] === 'string' ? data[provider.city].trim() : '';
        return { zip, countryCode: 'BR', provinceCode, ...(city ? { city } : {}) };
      }
      throw new Error('lookup');
    }

    renderShippingRates(rates, output) {
      output.replaceChildren();
      if (!rates.length) { output.textContent = this.strings.shippingEmpty; return; }
      const list = document.createElement('ul');
      list.className = 'rp-shipping-rates';
      for (const rate of rates) {
        const line = document.createElement('li');
        if (rate.amount === 0) line.classList.add('is-free');
        const name = document.createElement('span');
        name.textContent = rate.title;
        if (rate.description) {
          const description = document.createElement('small');
          description.textContent = rate.description;
          name.append(description);
        }
        const price = document.createElement('strong');
        price.textContent = rate.amount === 0 ? this.strings.shippingFree : new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', { style: 'currency', currency: rate.currency }).format(rate.amount);
        line.append(name, price);
        list.append(line);
      }
      const note = document.createElement('p');
      note.className = 'rp-shipping-result-note';
      note.textContent = this.strings.shippingResultNote;
      output.append(list, note);
    }

    async shipping(event) {
      event.preventDefault();
      this.cancelShipping();
      const form = event.currentTarget;
      const output = form.querySelector('[data-rp-shipping-result]');
      const zip = form.elements.zip.value.replace(/\D/g, '');
      if (!/^\d{8}$/.test(zip) || /^(\d)\1{7}$/.test(zip)) {
        output.textContent = this.strings.zipError;
        form.elements.zip.setAttribute('aria-invalid', 'true');
        form.elements.zip.focus();
        return;
      }
      if (this.loading || this.selectionError || !this.state.available || !this.state.variantId) {
        output.textContent = this.strings.shippingUnavailable;
        return;
      }
      const variant = String(this.state.variantId);
      const key = `${variant}:${zip}`;
      const now = Date.now();
      for (const [cachedKey, cached] of this.shippingCache) {
        if (cached.expires <= now) this.shippingCache.delete(cachedKey);
      }
      const cached = this.shippingCache.get(key);
      if (cached) { this.renderShippingRates(cached.rates, output); return; }
      const request = new AbortController();
      this.shippingRequest = request;
      let timedOut = false;
      const timeout = setTimeout(() => { timedOut = true; request.abort(); }, 25000);
      this.shippingTimeout = timeout;
      const current = () => this.shippingRequest === request && this.isConnected && String(this.state.variantId) === variant && form.elements.zip.value.replace(/\D/g, '') === zip;
      this.setShippingBusy(true);
      output.textContent = this.strings.loading;
      try {
        const address = await this.shippingAddress(zip, request.signal);
        if (!current() || request.signal.aborted) return;
        const url = new URL('https://ssot-api-dev-482129139926.us-central1.run.app/v1/storefront/shipping-rates');
        url.search = new URLSearchParams({ variantId: variant, zip, provinceCode: address.provinceCode });
        const response = await fetch(url, { credentials: 'omit', signal: request.signal });
        const quote = await response.json();
        if (!response.ok) throw new Error(quote.error === 'unavailable' ? 'unavailable' : quote.error === 'invalid-zip' ? 'invalid-zip' : 'quote');
        if (!current() || request.signal.aborted) return;
        if (!Array.isArray(quote.rates) || quote.rates.some(rate =>
          typeof rate.title !== 'string' || typeof rate.description !== 'string' ||
          !Number.isFinite(rate.amount) || rate.amount < 0 || rate.currency !== 'BRL'
        )) throw new Error('rate');
        const rates = quote.rates;
        this.renderShippingRates(rates, output);
        if (rates.length) this.shippingCache.set(key, { rates, expires: Date.now() + 300000 });
      } catch (error) {
        if (!current() || (error.name === 'AbortError' && !timedOut)) return;
        const invalidZip = error.message === 'invalid-zip';
        output.textContent = invalidZip ? this.strings.zipError : error.message === 'unavailable' ? this.strings.shippingUnavailable : this.strings.shippingError;
        if (invalidZip) form.elements.zip.setAttribute('aria-invalid', 'true');
      } finally {
        clearTimeout(timeout);
        if (this.shippingRequest === request) {
          this.shippingRequest = null;
          this.shippingTimeout = null;
          this.setShippingBusy(false);
        }
      }
    }

    async recentProducts() {
      try {
        const stored = JSON.parse(localStorage.getItem('reise_recent_products') || '[]');
        const handles = (Array.isArray(stored) ? stored : []).filter(handle => typeof handle === 'string' && /^[a-z0-9-]+$/.test(handle) && handle !== this.dataset.productHandle).slice(0, 6);
        localStorage.setItem('reise_recent_products', JSON.stringify([this.dataset.productHandle, ...handles].slice(0, 7)));
        const wrapper = document.querySelector('[data-rp-recent]');
        const list = wrapper?.querySelector('[data-rp-recent-list]');
        if (!list || !handles.length) return;
        const signal = this.signal;
        const observer = new IntersectionObserver(async entries => {
          if (signal.aborted || !this.isConnected || !entries.some(entry => entry.isIntersecting)) return;
          observer.disconnect();
          const cards = await Promise.allSettled(handles.map(async handle => {
            const response = await fetch(`${this.dataset.rootUrl}products/${encodeURIComponent(handle)}?view=reise_card`, { signal });
            if (!response.ok) throw new Error();
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const card = html.querySelector('.rn-site.rn-shell > article[data-rn-card]');
            if (!card || card.dataset.productHandle !== handle || !card.querySelector('[data-rn-product-json]')) throw new Error();
            return document.importNode(card.parentElement, true);
          }));
          if (signal.aborted || !this.isConnected || !list.isConnected) return;
          list.replaceChildren(...cards.filter(result => result.status === 'fulfilled').map(result => result.value));
          wrapper.hidden = !list.children.length;
          if (!wrapper.hidden) list.dispatchEvent(new CustomEvent('reise:products-loaded', { bubbles: true }));
        }, { rootMargin: '400px' });
        observer.observe(wrapper.parentElement);
        this.signal.addEventListener('abort', () => observer.disconnect(), { once: true });
      } catch {}
    }
  }
  customElements.define('reise-product', ReiseProduct);
})();
